import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ArrowLeft, Save, User, Shield, Upload, AlertTriangle, CheckCircle } from 'lucide-react'
import { LoadingInline } from '@/components/ui/spinner'
import { getInitials, formatPhone } from '@/lib/utils'
import { logger } from '@/lib/logger'
import { useAuth } from '@/hooks/use-auth'
import { useVerificarEmail } from '@/hooks/use-verificar-email'
import type { PapelUsuario } from '@/types'

interface NovoMembroFormData {
  nome: string
  email: string
  telefone: string
  papel: PapelUsuario
  avatar_url: string
}

const PAPEIS_OPCOES: { value: PapelUsuario; label: string; description: string }[] = [
  { value: 'comum', label: 'Membro Comum', description: 'Acesso básico às funcionalidades' },
  { value: 'gestor', label: 'Gestor', description: 'Pode gerenciar ministérios e eventos' },
  { value: 'financeiro', label: 'Financeiro', description: 'Acesso ao módulo financeiro' },
  { value: 'admin', label: 'Administrador', description: 'Acesso completo ao sistema' },
]

export function MembrosNovoPage() {
  const navigate = useNavigate()
  const { perfil } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [emailToVerify, setEmailToVerify] = useState<string>('')
  const [formData, setFormData] = useState<NovoMembroFormData>({
    nome: '',
    email: '',
    telefone: '',
    papel: 'comum',
    avatar_url: '',
  })

  // Hook para verificar se email já existe - só executa quando emailToVerify é definido
  const { data: usuarioExistente, isLoading: verificandoEmail } = useVerificarEmail(emailToVerify)

  // Verificar se email já existe quando usuário sair do campo
  useEffect(() => {
    if (emailToVerify && usuarioExistente) {
      setEmailError(`Este email já está cadastrado para ${usuarioExistente.nome} na igreja ${usuarioExistente.igreja.nome}`)
    } else if (emailToVerify && !verificandoEmail && !usuarioExistente) {
      setEmailError(null)
    }
  }, [usuarioExistente, emailToVerify, verificandoEmail])

  const handleInputChange = (field: keyof NovoMembroFormData, value: string) => {
    let processedValue = value
    
    // Apply phone mask if field is telefone
    if (field === 'telefone') {
      processedValue = formatPhone(value)
    }
    
    setFormData((prev) => ({ ...prev, [field]: processedValue }))
  }

  const handleEmailBlur = () => {
    if (formData.email && formData.email !== emailToVerify) {
      setEmailToVerify(formData.email)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Verificar se email já existe
    if (usuarioExistente) {
      return
    }

    // Verificar se usuário tem igreja
    if (!perfil?.igreja_id) {
      console.error('Usuário não tem igreja associada')
      return
    }

    setIsSubmitting(true)

    try {
      // Chamar a edge function para criar o usuário
      const response = await fetch('https://tkmsdopgnwyuuwphmczm.supabase.co/functions/v1/criar-usuario', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRrbXNkb3Bnbnd5dXV3cGhtY3ptIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjEzMTYwODksImV4cCI6MjA3Njg5MjA4OX0.QMHxTzB7uherl4I8HLIUaqT_-_Q18XlkPW40Q8TPr2E`,
        },
        body: JSON.stringify({
          nome: formData.nome,
          email: formData.email,
          telefone: formData.telefone,
          igreja_id: perfil.igreja_id,
          permissao: formData.papel === 'comum' ? 'membro' : formData.papel,
          ativo: true,
          senha_temporaria: '123456'
        })
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Erro ao criar membro')
      }

      logger.info('Novo membro criado:', result)

      // Redirecionar para a lista de membros
      navigate('/membros')
    } catch (error) {
      console.error('Erro ao criar membro:', error)
      // TODO: Mostrar erro para o usuário
    } finally {
      setIsSubmitting(false)
    }
  }

  const generateAvatarUrl = (nome: string) => {
    const seed = nome.replace(/\s+/g, '').toLowerCase()
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`
  }

  const handleAvatarGenerate = () => {
    if (formData.nome) {
      const newAvatarUrl = generateAvatarUrl(formData.nome)
      handleInputChange('avatar_url', newAvatarUrl)
    }
  }

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-6 lg:py-8 xl:px-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="h-8 w-8 p-0"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Novo Membro</h1>
          <p className="text-muted-foreground">Adicione um novo membro à igreja</p>
        </div>
      </motion.div>

      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Formulário Principal */}
          <div className="space-y-6 lg:col-span-2">
            {/* Informações Pessoais */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Informações Pessoais
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome Completo *</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => handleInputChange('nome', e.target.value)}
                      placeholder="Digite o nome completo"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onBlur={handleEmailBlur}
                      placeholder="exemplo@email.com"
                      required
                      className={emailError ? 'border-red-500' : ''}
                    />
                    {verificandoEmail && emailToVerify && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <LoadingInline size="sm" />
                        Verificando email...
                      </div>
                    )}
                    {emailError && (
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{emailError}</AlertDescription>
                      </Alert>
                    )}
                    {emailToVerify && !verificandoEmail && !usuarioExistente && !emailError && (
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        Email disponível
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => handleInputChange('telefone', e.target.value)}
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Permissões */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Permissões e Acesso
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="papel">Papel no Sistema *</Label>
                  <Select
                    value={formData.papel}
                    onValueChange={(value: string) =>
                      handleInputChange('papel', value as PapelUsuario)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o papel" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAPEIS_OPCOES.map((papel) => (
                        <SelectItem key={papel.value} value={papel.value}>
                          <div className="flex flex-col">
                            <span className="font-medium">{papel.label}</span>
                            <span className="text-xs text-muted-foreground">
                              {papel.description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Avatar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Foto do Perfil
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col items-center space-y-4">
                  <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                    <AvatarImage src={formData.avatar_url || undefined} />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-xl text-white">
                      {formData.nome ? (
                        getInitials(formData.nome)
                      ) : (
                        <User className="h-8 w-8" />
                      )}
                    </AvatarFallback>
                  </Avatar>

                  <div className="w-full space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAvatarGenerate}
                      disabled={!formData.nome}
                      className="w-full"
                    >
                      Gerar Avatar
                    </Button>
                    <div className="space-y-2">
                      <Label htmlFor="avatar_url" className="text-xs">
                        URL da Imagem
                      </Label>
                      <Input
                        id="avatar_url"
                        value={formData.avatar_url}
                        onChange={(e) => handleInputChange('avatar_url', e.target.value)}
                        placeholder="https://..."
                        className="text-xs"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Resumo */}
            <Card>
              <CardHeader>
                <CardTitle>Resumo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm">
                  <span className="text-muted-foreground">Nome:</span>
                  <p className="font-medium">{formData.nome || 'Não informado'}</p>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">E-mail:</span>
                  <p className="font-medium">{formData.email || 'Não informado'}</p>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">Papel:</span>
                  <p className="font-medium capitalize">
                    {PAPEIS_OPCOES.find((p) => p.value === formData.papel)?.label || formData.papel}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col gap-3 sm:flex-row sm:justify-end"
        >
          <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={isSubmitting}
            >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !formData.nome || !formData.email || !!emailError}
            className="shadow-md shadow-primary/20"
          >
            {isSubmitting ? (
              <LoadingInline message="Criando..." size="sm" />
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Criar Membro
              </>
            )}
          </Button>
        </motion.div>
      </motion.form>
    </div>
  )
}
